import { useCallback, useEffect, useMemo, useState } from "react";
import { FaBars } from "react-icons/fa6";
import { Table } from "reactstrap";
import { Pageable } from "../../../types/common";
import { Action } from "../../../types/reducer-action";
import { IValueInfo } from "../../../types/valuelist";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import PaginationBar from "../../common/pagination-bar";
import TableHeaders, { TableHeaderInfo } from "../../common/table-header";
import TableRow from "../../common/table-row";
import { useValueListDispatch, useValueListState } from "./value-list-manager-context";

const TABLE_HEADERS: TableHeaderInfo[] = [
  { header: 'Value Code' },
  { header: 'Value Name' },
  { header: 'Language' },
  { header: 'Parent Code' }
];

interface ValueListTableProps extends Pageable {
  sortable?: boolean;
}

const ValueListTable = (props: ValueListTableProps) => {
  const { sortable } = props;

  const tableHeaders = useMemo(() => sortable ? [{}, ...TABLE_HEADERS] : TABLE_HEADERS, [sortable]);

  const pageSize = sortable ? -1 : props.pageSize; // disable pagination for sorting

  const { dataSet, selectedDataId } = useValueListState();

  const dispatch = useValueListDispatch();

  const [sortedList, setSortedList] = useState<IValueInfo[]>();

  const [pagedData, setPagedData] = useState<IValueInfo[]>([]);

  const [moveBefore, setMoveBefore] = useState(0);

  const selectItem = (item: IValueInfo | undefined) =>
    dispatch(new Action(DataEntryEvent.SelectData, item?.id));

  useEffect(() => {
    selectItem(undefined);
    setSortedList(undefined);
  }, [pagedData]);

  useEffect(() => {
    const changeList = sortedList?.filter(u => dataSet?.find(o => +o.id === +u.id)?.displayOrder !== u.displayOrder);
    dispatch(new Action(DataEntryEvent.SetStateValues, { sortedItems: changeList }));
  }, [sortedList, dataSet]);

  const reorder = useCallback((rowId: number): IValueInfo[] | undefined => {
    const row = pagedData.find(v => +v.id === rowId);
    if (!row) return undefined;

    const arr: IValueInfo[] = [];
    pagedData.forEach(v => {
      if (+v.id === moveBefore) {
        arr.push(row);
      }

      if (+v.id !== rowId) {
        arr.push(v);
      }
    });

    return arr.map((v, i) => ({
      ...v,
      displayOrder: i + 1
    } as IValueInfo));
  }, [moveBefore]);

  const handleDragStart = (event: React.DragEvent<HTMLTableRowElement>) => {
    event.dataTransfer?.setData("text/plain", event.currentTarget.id);
  };

  const handleDragOver = (event: React.DragEvent<HTMLTableRowElement>) => {
    event.preventDefault();

    setMoveBefore(+event.currentTarget.id);
  };

  const handleDrop = (event: React.DragEvent<HTMLTableElement>) => {
    const droppedRowId = event.dataTransfer?.getData("text/plain");
    if (!droppedRowId) return;

    setSortedList(reorder(+droppedRowId));

    setMoveBefore(0);
  };

  return (
    <section aria-label="Value List" className={`${sortable && 'overflow-y-auto scrollable-value-list'}`}>
      <Table striped hover onDrop={handleDrop}>
        <TableHeaders headers={tableHeaders} />

        {dataSet && dataSet.length > 0
          ?
          <tbody>
            {(sortedList ?? pagedData).map((item) =>
              <TableRow key={item.id} id={item.id.toString()}
                selected={item.id === selectedDataId} onSelect={() => selectItem(item)}
                draggable onDragStart={handleDragStart} onDragOver={handleDragOver}>
                {sortable &&
                  <td className="small order-column pe-force" aria-label={`Display order: ${item.displayOrder ?? 'unknown'}`}>
                    <FaBars title="Drag to sort" />
                  </td>
                }
                <th scope="row">{item?.code}</th>
                <td>{item?.label}</td>
                <td>{item?.lang}</td>
                <td>{item?.parent?.code}</td>
              </TableRow>
            )}
          </tbody>
          :
          <tfoot>
            <tr>
              <td colSpan={tableHeaders.length} className="text-center">
                Search results will appear in this table.
              </td>
            </tr>
          </tfoot>
        }
      </Table>

      <PaginationBar data={dataSet} pageSize={pageSize} onPageChange={setPagedData} />
    </section>
  );
};

export default ValueListTable;
